import { Loader } from "shared/components/Loader";
import { EmptyPropertyGroups } from "./components/EmptyPropertyGroups";
import { PropertyGroupsTable } from "./components/PropertyGroupsTable";
import { useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { PROPERTY_GROUPS_QUERY_KEY } from "./constants";
import { useState } from "react";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { useAuthContext } from "providers/Auth";
import { Layout } from "providers/Layout";
import { InputSize } from "shared/components/forms/Input";
import { ButtonSize, ButtonVariant } from "shared/components/Button";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { Show } from "shared/components/helpers/Show";
import { Button } from "shared/components/Button";
import { useNavigate } from "react-router-dom";
import { Input } from "shared/components/forms/Input";
import ArrowSplit from "shared/icons/arrowSplit.svg?react";
import { RegroupWarningModal } from "./components/RegroupWarningModal";
import { QUERY_SETTINGS, getSettings } from "api/landlord/landlord";

export const PropertyGroups = () => {
  const { type } = useAuthContext();
  const navigate = useNavigate();
  const dictionary = useUnitsPropertiesDictionary();
  const { adminPrivileges } = type;
  const [searchParams, setSearchParams] = useState({
    search: ""
  });
  const [isRegroupModalOpen, setIsRegroupModalOpen] = useState(false);
  const [debouncedSearchTerm] = useDebouncedValue(searchParams.search, 500);

  const { data: config, isLoading: isLoadingConfig } = useQuery({
    queryKey: [PROPERTY_GROUPS_QUERY_KEY.CONFIG],
    queryFn: () =>
      instance.get("/portal/v1/property_groups/config").then((res) => res.data)
  });

  const { data: settingsData } = useQuery({
    queryKey: [QUERY_SETTINGS],
    queryFn: () => getSettings()
  });

  const { data: propertyGroups, isFetching: isFetchingPropertyGroups } =
    useQuery({
      queryKey: [
        PROPERTY_GROUPS_QUERY_KEY.PROPERTY_GROUPS,
        debouncedSearchTerm
      ],
      queryFn: () =>
        instance
          .get("/portal/v1/property_groups", {
            params: {
              search: debouncedSearchTerm
            }
          })
          .then((res) => res.data.property_groups)
    });

  // Check if partner is using Appfolio property groups
  const appfolioSettings = settingsData?.settings?.find(
    (s) => s.name === "appfolio_use_property_groups"
  );
  const isUsingAppfolioPropertyGroups =
    (appfolioSettings?.value as any) === true ||
    appfolioSettings?.value === "true";

  if (isLoadingConfig)
    return (
      <div className="flex justify-center items-center h-[100vh]">
        <Loader />
      </div>
    );

  if (
    propertyGroups?.length === 0 &&
    !debouncedSearchTerm &&
    adminPrivileges &&
    !isUsingAppfolioPropertyGroups
  )
    return <EmptyPropertyGroups />;

  const totalProperties = propertyGroups?.reduce(
    (acc, curr) => acc + curr.total_properties,
    0
  );

  return (
    <Layout sectionClassName="space-y-4 h-full">
      <div className="flex items-center justify-between">
        <div className="space-y-3">
          <h1 className="text-2xl font-bold">{dictionary.Property} groups</h1>
          <p className="text-sm text-content-400">
            {totalProperties} {dictionary.properties}
            {isUsingAppfolioPropertyGroups && (
              <span className="ml-2 text-xs text-content-500">
                (Managed by Appfolio)
              </span>
            )}
          </p>
        </div>
        <Show if={adminPrivileges && !isUsingAppfolioPropertyGroups}>
          <div className="flex gap-2">
            <Button
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              iconPosition="right"
              onClick={() => {
                navigate(
                  `/property-groups/configuration?step=${config?.split_by[0]}&edit=true`
                );
              }}
            >
              Edit groups
            </Button>

            <Button
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SM}
              Icon={ArrowSplit}
              iconPosition="right"
              onClick={() => setIsRegroupModalOpen(true)}
            >
              Regroup {dictionary.properties}
            </Button>
          </div>
        </Show>
        <Show if={isUsingAppfolioPropertyGroups && adminPrivileges}>
          <div className="text-sm text-content-500">
            <p>Property groups are managed by Appfolio integration.</p>
            <p>
              Configure group selection in Settings → Integration → Appfolio.
            </p>
          </div>
        </Show>
      </div>

      <div className="max-w-[271px] pt-2">
        <Input
          id="search"
          autoComplete="off"
          placeholder="Search group"
          size={InputSize.SM}
          value={searchParams.search}
          onChange={(e) =>
            setSearchParams({
              ...searchParams,
              search: e.target.value
            })
          }
        />
      </div>
      <PropertyGroupsTable
        availableSplitOptions={config?.split_by || []}
        propertyGroups={propertyGroups || []}
        searchTerm={searchParams.search}
        isLoading={isFetchingPropertyGroups}
        isUsingAppfolioPropertyGroups={isUsingAppfolioPropertyGroups}
      />

      <Show if={isRegroupModalOpen}>
        <RegroupWarningModal
          isOpen={isRegroupModalOpen}
          onClose={() => setIsRegroupModalOpen(false)}
        />
      </Show>
    </Layout>
  );
};
